import React, { FunctionComponent, ReactNode, useEffect, useRef, useState } from "react";
import { Check, ChevronDown, LucideIcon } from "lucide-react";
import "./PillTabs.scss";

export interface PillTab<K extends string = string> {
    key: K;
    label: ReactNode;
    icon?: LucideIcon;
    /** disabled visual */
    disabled?: boolean;
}

interface PillTabsProps<K extends string = string> {
    tabs: PillTab<K>[];
    value: K;
    onChange: (key: K) => void;
    /** acessibilidade — label do role tablist */
    ariaLabel?: string;
    className?: string;
    /**
     * No mobile (<= 600px) troca as pills por um dropdown; nos demais
     * breakpoints nada muda. Opt-in de proposito: as outras telas que usam
     * PillTabs continuam identicas.
     */
    collapseOnMobile?: boolean;
}

function PillTabs<K extends string>({
    tabs,
    value,
    onChange,
    ariaLabel,
    className = "",
    collapseOnMobile = false,
}: PillTabsProps<K>) {

    const [open, setOpen]               = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const rootRef                       = useRef<HTMLDivElement>(null);

    const selectedIndex = Math.max(0, tabs.findIndex((t) => t.key === value));
    const selected      = tabs[selectedIndex];

    // Ao abrir, o destaque do teclado comeca no item ja selecionado.
    useEffect(() => {
        if (open) setActiveIndex(selectedIndex);
    }, [open, selectedIndex]);

    // Fecha ao clicar fora ou ao rolar a pagina — a lista e ancorada no
    // trigger, entao rolar com ela aberta a deixaria deslocada.
    useEffect(() => {
        if (!open) return;

        const onPointerDown = (e: MouseEvent | TouchEvent) => {
            if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
        };
        const onScroll = () => setOpen(false);

        document.addEventListener("mousedown", onPointerDown);
        document.addEventListener("touchstart", onPointerDown);
        window.addEventListener("scroll", onScroll, true);
        window.addEventListener("resize", onScroll);

        return () => {
            document.removeEventListener("mousedown", onPointerDown);
            document.removeEventListener("touchstart", onPointerDown);
            window.removeEventListener("scroll", onScroll, true);
            window.removeEventListener("resize", onScroll);
        };
    }, [open]);

    const commit = (tab: PillTab<K>) => {
        if (tab.disabled) return;
        onChange(tab.key);
        setOpen(false);
    };

    /** Move o destaque pulando itens desabilitados. */
    const step = (from: number, dir: 1 | -1) => {
        for (let i = 1; i <= tabs.length; i++) {
            const next = (from + dir * i + tabs.length * i) % tabs.length;
            if (!tabs[next].disabled) return next;
        }
        return from;
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
        switch (e.key) {
            case "ArrowDown":
            case "ArrowUp":
                e.preventDefault();
                if (!open) { setOpen(true); return; }
                setActiveIndex((i) => step(i, e.key === "ArrowDown" ? 1 : -1));
                return;
            case "Enter":
            case " ":
                e.preventDefault();
                if (open) commit(tabs[activeIndex]);
                else setOpen(true);
                return;
            case "Escape":
                if (open) { e.preventDefault(); setOpen(false); }
                return;
            case "Home":
                if (open) { e.preventDefault(); setActiveIndex(step(-1, 1)); }
                return;
            case "End":
                if (open) { e.preventDefault(); setActiveIndex(step(0, -1)); }
                return;
        }
    };

    const pills = (
        <div
            className={collapseOnMobile ? "ui-pill-tabs" : `ui-pill-tabs ${className}`.trim()}
            role="tablist"
            aria-label={ariaLabel}
        >
            {tabs.map((tab) => {
                const Icon = tab.icon;
                const active = tab.key === value;
                return (
                    <button
                        key={tab.key}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        disabled={tab.disabled}
                        className={`ui-pill-tab ${active ? "active" : ""}`}
                        onClick={() => onChange(tab.key)}
                    >
                        {Icon && <Icon size={16} />}
                        <span>{tab.label}</span>
                    </button>
                );
            })}
        </div>
    );

    if (!collapseOnMobile) return pills;

    const SelectedIcon = selected?.icon;
    const listId = "ui-pill-select-list";

    // Dropdown proprio em vez de <select> nativo: o popup nativo e desenhado
    // pelo sistema operacional, entao nem a posicao nem o tamanho da fonte sao
    // controlaveis por CSS — em janela estreita ele abre deslocado e minusculo.
    //
    // Os dois formatos ficam no DOM e o CSS decide qual aparece. Quem estiver
    // com `display: none` sai da ordem de tabulacao e da arvore de
    // acessibilidade, entao nao ha foco duplicado nem leitura repetida.
    return (
        <div className={`ui-pill-tabs-responsive ${className}`.trim()}>
            {pills}

            <div className="ui-pill-select" ref={rootRef} onKeyDown={onKeyDown}>
                <button
                    type="button"
                    className={`ui-pill-select-trigger ${open ? "open" : ""}`}
                    aria-haspopup="listbox"
                    aria-expanded={open}
                    aria-controls={open ? listId : undefined}
                    aria-label={ariaLabel}
                    onClick={() => setOpen((o) => !o)}
                >
                    <span className="ui-pill-select-value">
                        {SelectedIcon && <SelectedIcon size={16} />}
                        {selected?.label}
                    </span>
                    <ChevronDown size={16} className="ui-pill-select-caret" aria-hidden="true" />
                </button>

                {open && (
                    <ul className="ui-pill-select-list" id={listId} role="listbox" aria-label={ariaLabel}>
                        {tabs.map((tab, i) => {
                            const Icon = tab.icon;
                            const isSelected = tab.key === value;
                            return (
                                <li
                                    key={tab.key}
                                    role="option"
                                    aria-selected={isSelected}
                                    aria-disabled={tab.disabled || undefined}
                                    className={[
                                        "ui-pill-select-option",
                                        isSelected ? "selected" : "",
                                        i === activeIndex ? "active" : "",
                                        tab.disabled ? "disabled" : "",
                                    ].filter(Boolean).join(" ")}
                                    onMouseEnter={() => setActiveIndex(i)}
                                    onClick={() => commit(tab)}
                                >
                                    {Icon && <Icon size={16} />}
                                    <span>{tab.label}</span>
                                    {isSelected && <Check size={15} className="tick" />}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
}

const Typed: <K extends string>(p: PillTabsProps<K>) => React.ReactElement = PillTabs;
export default Typed as FunctionComponent<PillTabsProps<any>>;
